import { requestFile, requestForm } from "./client";
import { newIdempotencyKey } from "./orders";
import type {
  BulkCommitResponse,
  BulkPreview,
  InventoryFamily,
  ProductStatus,
} from "./types";

/**
 * Bulk product import and export.
 *
 * THE FILE IS SENT TWICE, AND THAT IS THE DESIGN. Validation answers what the
 * workbook would do and writes nothing; confirmation sends the SAME workbook
 * back with the digest validation returned, and the server re-derives
 * everything from the catalogue as it is at that moment. So this client never
 * holds a plan, never describes an operation, and cannot ask for one — it can
 * only ask for a file to be applied, which is the only thing a browser should
 * be able to ask for on an operation that rewrites a catalogue.
 *
 * There is no upload session and no interim server state to clean up: the
 * browser keeps the File it already has.
 */

export interface BulkExportQuery {
  family?: InventoryFamily;
  category?: string;
  status?: ProductStatus;
  search?: string;
}

export interface DownloadedFile {
  blob: Blob;
  filename: string;
  /** Counts the server reports in headers, so the screen can say what it
   *  downloaded without opening the workbook it just handed over. */
  products: number | null;
  rows: number | null;
}

function counted(file: { blob: Blob; filename: string; headers: Headers }):
    DownloadedFile {
  const read = (name: string) => {
    const raw = file.headers.get(name);
    const value = raw === null ? Number.NaN : Number(raw);
    return Number.isFinite(value) ? value : null;
  };
  return {
    blob: file.blob,
    filename: file.filename,
    products: read("X-OptiQ-Product-Count"),
    rows: read("X-OptiQ-Row-Count"),
  };
}

export const bulkProductsApi = {
  /** The empty workbook and its contract, generated from the import schema. */
  template: async (): Promise<DownloadedFile> =>
    counted(await requestFile("/admin/bulk-products/template")),

  /** What this workbook would do. Writes nothing. */
  validate: (file: File): Promise<BulkPreview> => {
    const form = new FormData();
    form.append("file", file, file.name);
    return requestForm<BulkPreview>("/admin/bulk-products/validate", form);
  },

  /**
   * Apply it, once.
   *
   * The key is generated per confirmation, so a second key only exists because
   * an operator pressed the button again — which is a second intent and is
   * refused if the plan changed. What it protects against is the confirmation
   * that was delivered and whose answer was lost.
   */
  commit: (file: File, planDigest: string,
           idempotencyKey?: string): Promise<BulkCommitResponse> => {
    const form = new FormData();
    form.append("file", file, file.name);
    form.append("plan_digest", planDigest);
    return requestForm<BulkCommitResponse>(
      "/admin/bulk-products/commit", form,
      { "Idempotency-Key": idempotencyKey ?? newIdempotencyKey() });
  },

  /** The catalogue as a workbook. Every filter is applied by the server. */
  export: async (query: BulkExportQuery = {}): Promise<DownloadedFile> =>
    counted(await requestFile("/admin/bulk-products/export",
                              { ...query })),
};
